import {OntologyListData, OntologyListEntity, RelatableSelectableItem, SelectableItem} from './FilterBannerModels';
import {buildNodePath} from './UpdatedFilterBannerHelpers';
import {
  InvoiceStatus,
  LagPeriod,
  MemberBillingArea,
  MemberLocation,
  PayerCategoryLevel,
  PayerObject,
  VisitType
} from '../shared/models';
import {NodeExpansionState, PayerType} from '../shared/enums';

export function setUpOntologyListData(ontologyList: OntologyListEntity[]): OntologyListData {
  const listOfAllDepartments: RelatableSelectableItem<OntologyListEntity>[] = [];
  const listOfAllSpecialties: RelatableSelectableItem<OntologyListEntity>[] = [];
  const listOfAllProviders: RelatableSelectableItem<OntologyListEntity>[] = [];
  const departments: Map<string, RelatableSelectableItem<OntologyListEntity>> =
    new Map<string, RelatableSelectableItem<OntologyListEntity>>();
  const specialties: Map<string, RelatableSelectableItem<OntologyListEntity>> =
    new Map<string, RelatableSelectableItem<OntologyListEntity>>();
  let firstDepartmentOccurrence = false;
  let firstSpecialtyOccurrence = false;
  ontologyList.forEach(item => {
    const pathArray = item.nodePath.split('\\');
    let department: RelatableSelectableItem<OntologyListEntity> | undefined;
    department = departments.get(pathArray[2]);
    firstDepartmentOccurrence = !department;
    department = department || {
      item: {
        selected: false,
        belongs: true,
        matchesSearchText: true,
        item: {
          nodePath: buildNodePath(pathArray, 0, 3),
          departmentNodeName: item.departmentNodeName
        },
        key: +pathArray[2]
      },
      relatives: []
    };
    departments.set(pathArray[2], department);
    let specialty: RelatableSelectableItem<OntologyListEntity> | undefined;
    specialty = specialties.get(pathArray[3]);
    firstSpecialtyOccurrence = !specialty;
    specialty = specialty || {
      item: {
        selected: false,
        belongs: true,
        matchesSearchText: true,
        item: {
          nodePath: buildNodePath(pathArray, 0, 4),
          specialtyNodeName: item.specialtyNodeName
        },
        key: +pathArray[3]
      },
      relatives: []
    };
    specialties.set(pathArray[3], specialty);
    let provider: RelatableSelectableItem<OntologyListEntity> | undefined;
    provider = {
      item: {
        selected: false,
        belongs: false,
        matchesSearchText: true,
        item: {
          nodePath: buildNodePath(pathArray, 0, 5),
          providerNodeName: item.providerNodeName,
          specialtyNodeName: item.specialtyNodeName,
          departmentNodeName: item.departmentNodeName
        },
        key: +pathArray[4]
      },
      relatives: [department.item, specialty.item]
    };
    if (firstDepartmentOccurrence) {
      department.relatives = [specialty.item, provider.item];
      listOfAllDepartments.push(department);
    } else {
      [specialty.item, provider.item].forEach(relative => {
        department?.relatives.push(relative);
      });
    }
    if (firstSpecialtyOccurrence) {
      specialty.relatives = [department.item, provider.item];
      listOfAllSpecialties.push(specialty);
    } else {
      [department.item, provider.item].forEach(relative => {
        specialty?.relatives.push(relative);
      });
    }
    provider.relatives = [department.item, specialty.item];
    listOfAllProviders.push(provider);
  });
  return {
    allDepartmentsNodePath: ontologyList.length ? buildNodePath(ontologyList[0].nodePath.split('\\'),
      0, 2) : '',
    departments: listOfAllDepartments,
    specialties: listOfAllSpecialties,
    providers: listOfAllProviders
  };
}

export function setUpLocationList(locations: MemberLocation[]):
  RelatableSelectableItem<MemberLocation>[] {
  return locations.map(location => {
    const relatableLocation: RelatableSelectableItem<MemberLocation> = {
      item: {
        selected: false,
        belongs: true,
        matchesSearchText: true,
        displayText: location.memberLocationName,
        item: location,
        key: location.memberLocationKey
      },
      relatives: []
    };
    return relatableLocation;
  });
}

export function setUpInvoiceList(invoiceStati: InvoiceStatus[]):
  RelatableSelectableItem<InvoiceStatus>[] {
  return invoiceStati.map(status => {
    const relatableInvoiceStatus: RelatableSelectableItem<InvoiceStatus> = {
      item: {
        selected: false,
        belongs: true,
        matchesSearchText: true,
        displayText: status.statusText,
        item: status,
        key: status.statusId
      },
      relatives: []
    };
    return relatableInvoiceStatus;
  });
}

export function setUpLagPeriodList(lagPeriods: LagPeriod[]):
  RelatableSelectableItem<LagPeriod>[] {
  return lagPeriods.map(lag => {
    const relatableLagPeriod: RelatableSelectableItem<LagPeriod> = {
      item: {
        selected: false,
        belongs: true,
        matchesSearchText: true,
        displayText: lag.name,
        item: lag,
        key: lag.key
      },
      relatives: []
    };
    return relatableLagPeriod;
  });
}

export function setUpBillingAreaList(billingAreas: MemberBillingArea[]):
  RelatableSelectableItem<MemberBillingArea>[] {
  return billingAreas.map(billingArea => {
    const relatableBillingArea: RelatableSelectableItem<MemberBillingArea> = {
      item: {
        selected: false,
        belongs: true,
        matchesSearchText: true,
        displayText: billingArea.memberBillingAreaDescription,
        item: billingArea,
        key: billingArea.memberBillingAreaKey
      },
      relatives: []
    };
    return relatableBillingArea;
  });
}

export function setUpVisitTypes(visitTypes: VisitType[]): RelatableSelectableItem<VisitType>[] {
  return visitTypes.map(visitType => {
    const relatableVisitType: RelatableSelectableItem<VisitType> = {
      item: {
        selected: false,
        belongs: true,
        matchesSearchText: true,
        displayText: visitType.text,
        item: visitType,
        key: visitType.key
      },
      relatives: []
    };
    return relatableVisitType;
  });
}

export function setUpPayerHierarchy(payers: PayerCategoryLevel[]):
  RelatableSelectableItem<PayerObject>[] {
  const items: RelatableSelectableItem<PayerObject>[] = [];
  payers.forEach(payerCategory => {
    const {payerCategoryKey, payerCategoryDesc, nationalPayers} = payerCategory;
    const categoryItem: SelectableItem<PayerObject> = {
      selected: false,
      belongs: true,
      matchesSearchText: true,
      item: {
        level: PayerType.CATEGORY,
        key: payerCategoryKey,
        description: payerCategoryDesc,
        payerCriteria: {
          payerCategoryKey: payerCategoryKey,
          payerCategoryDesc: payerCategoryDesc
        }
      },
      key: payerCategoryKey,
      expansion: {
        state: (nationalPayers.length ? NodeExpansionState.COLLAPSED : NodeExpansionState.LEAF),
        depth: 0
      },
      displayText: payerCategoryDesc
    };
    const categoryRelatable: RelatableSelectableItem<PayerObject> = {
      item: categoryItem,
      relatives: []
    };
    items.push(categoryRelatable);
    nationalPayers.forEach(nationalPayer => {
      const {nationalPayerKey, nationalPayerDesc, memberPayers} = nationalPayer;
      const nationalItem: SelectableItem<PayerObject> = {
        selected: false,
        belongs: true,
        matchesSearchText: true,
        item: {
          level: PayerType.NATIONAL,
          key: nationalPayerKey,
          description: nationalPayerDesc,
          payerCriteria: {
            payerCategoryKey: payerCategoryKey,
            payerCategoryDesc: payerCategoryDesc,
            nationalPayerKey: nationalPayerKey,
            nationalPayerDesc: nationalPayerDesc
          },
        },
        key: nationalPayerKey,
        expansion: {
          state: (memberPayers.length ? NodeExpansionState.COLLAPSED : NodeExpansionState.LEAF),
          depth: 1
        },
        displayText: nationalPayerDesc
      };
      categoryRelatable.relatives.push(nationalItem);
      const nationalRelatable = {
        item: nationalItem,
        relatives: [categoryItem]
      };
      items.push(nationalRelatable);
      memberPayers.forEach(memberPayer => {
        const {memberPayerKey, memberPayerDesc} = memberPayer;
        const memberItem: SelectableItem<PayerObject> = {
          selected: false,
          belongs: true,
          matchesSearchText: true,
          item: {
            level: PayerType.MEMBER,
            key: memberPayerKey,
            description: memberPayerDesc,
            payerCriteria: {
              payerCategoryKey: payerCategoryKey,
              payerCategoryDesc: payerCategoryDesc,
              nationalPayerKey: nationalPayerKey,
              nationalPayerDesc: nationalPayerDesc,
              memberPayerKey: memberPayerKey,
              memberPayerDesc: memberPayerDesc
            }
          },
          key: memberPayerKey,
          expansion: {
            state: NodeExpansionState.LEAF,
            depth: 2
          },
          displayText: memberPayerDesc
        };
        categoryRelatable.relatives.push(memberItem);
        nationalRelatable.relatives.push(memberItem);
        const memberRelatable: RelatableSelectableItem<PayerObject> = {
          item: memberItem,
          relatives: [categoryItem, nationalItem]
        };
        items.push(memberRelatable);
      });
    });
  });
  return items;
}
